// HOW A LIST IS ORGANISED ON SCREEN AND ON PAPER — one module, so the panel, the phone
// rows and the print page group, order and filter the same rows the same way.
//
// The setting lives on the list (migration 079, `list.view`): grouped by its states, by
// one of its TEXT columns, or not at all, and the order inside a group — as the rows were
// added, or by name (א–ת). Nothing here is a figure the server keeps: the bar and the
// state counts stay the server's summary; a group's count is only its rows.
import he from '../../i18n/he';

// A category typed by hand: "מנהלים" and " מנהלים " are one group, whatever the spacing.
export const categoryText = (v) => String(v ?? '').replace(/\s+/g, ' ').trim();
const categoryKey = (v) => categoryText(v).toLowerCase();
const byHebrew = (a, b) => a.localeCompare(b, 'he', { sensitivity: 'base', numeric: true });

const NO_VIEW = { group_by: 'none', group_column_id: null, sort: 'added' };

// The list's grouping, resolved against its columns: `{ mode, column, sort }`. A grouping
// column that is gone or no longer text reads as none (the server says the same).
export const groupingOf = (list) => {
  const view = list.view || NO_VIEW;
  const column = view.group_by === 'column'
    ? list.columns.find((c) => c.id === view.group_column_id && c.kind === 'text') : null;
  const mode = column ? 'column' : (view.group_by === 'state' ? 'state' : 'none');
  return { mode, column, sort: view.sort === 'name' ? 'name' : 'added' };
};

// Which group a row belongs to. The key is stable and never shown.
export const groupKeyOf = (item, grouping) => {
  if (grouping.mode === 'state') return `s:${item.state_id}`;
  if (grouping.mode === 'column') return `c:${categoryKey(item.cells[grouping.column.id])}`;
  return 'all';
};

// The rows in their order inside a group: as added (the order the server sends), or by name.
export const sortRows = (items, sort) => (sort === 'name'
  ? [...items].sort((a, b) => byHebrew(a.name, b.name))
  : items);

// The groups of these rows, each { key, label, value, items, finished }, in the list's
// order: by state the states' own order; by a column א–ת, with the rows that have no value
// last, under "ללא <column>". A group with no rows is not listed. Ungrouped: one group.
export const groupRows = (items, list) => {
  const grouping = groupingOf(list);
  const finishedIds = new Set(list.states.filter((s) => s.is_finished).map((s) => s.id));
  const make = (key, label, value, rows) => ({
    key, label, value, items: sortRows(rows, grouping.sort),
    finished: rows.filter((r) => finishedIds.has(r.state_id)).length,
  });
  if (grouping.mode === 'none') return [make('all', null, null, items)];

  const buckets = new Map();
  for (const it of items) {
    const key = groupKeyOf(it, grouping);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(it);
  }
  if (grouping.mode === 'state') {
    return list.states
      .filter((s) => buckets.has(`s:${s.id}`))
      .map((s) => ({ ...make(`s:${s.id}`, s.label, s.id, buckets.get(`s:${s.id}`)), tone: s.tone }));
  }
  const col = grouping.column.id;
  const named = [...buckets.entries()].filter(([key]) => key !== 'c:')
    .map(([key, rows]) => make(key, categoryText(rows[0].cells[col]), categoryText(rows[0].cells[col]), rows))
    .sort((a, b) => byHebrew(a.label, b.label));
  const empty = buckets.has('c:') ? [make('c:', he.lists.noValue(grouping.column.label), '', buckets.get('c:'))] : [];
  return [...named, ...empty];
};

// The group chips: every group of the WHOLE list with its count, so a filter never hides
// the other groups you could switch to.
export const groupChoices = (list) => (groupingOf(list).mode === 'none' ? [] : groupRows(list.items, list));

// A new row added while one group is shown lands in that group: its state, or its value in
// the grouping column. Without this a row typed into a filtered list would vanish from it.
export const presetFor = (list, groupKey) => {
  if (!groupKey) return null;
  const grouping = groupingOf(list);
  const group = groupChoices(list).find((g) => g.key === groupKey);
  if (!group) return null;
  if (grouping.mode === 'state') return { label: group.label, row: { state_id: group.value } };
  if (grouping.mode === 'column' && group.value) return { label: group.label, row: { cells: { [grouping.column.id]: group.value } } };
  return null;
};

// The values already typed in a column, for the suggestions offered while typing in it —
// so a category is chosen, not retyped three different ways.
export const columnValues = (items, columnId) => {
  const seen = new Map();
  for (const it of items) {
    const v = categoryText(it.cells[columnId]);
    if (v && !seen.has(v.toLowerCase())) seen.set(v.toLowerCase(), v);
  }
  return [...seen.values()].sort(byHebrew);
};
